import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  IconButton,
  Stack,
  Box,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DashboardLayout from "../../../layout/DashboardLayout";
import {
  fetchProducts,
  addProduct,
  deleteProduct,
  hideProduct,
  reorderProducts,
} from "../../../api/auth";

const schema = yup.object({
  title: yup.string().required(),
  description: yup.string().required(),
});

export default function ProductPage() {
  const [products, setProducts] = useState([]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const loadProducts = async () => {
    try {
      const res = await fetchProducts();
      setProducts(res.data);
    } catch (err) {
      toast.error("Failed to load products");
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const handleAddProduct = async (data) => {
    try {
      await addProduct(data);
      toast.success("Product added");
      reset();
      loadProducts();
    } catch (err) {
      toast.error("Failed to add product");
    }
  };

  const handleDeleteProduct = async (id) => {
    try {
      await deleteProduct(id);
      toast.success("Product deleted");
      loadProducts();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleHideProduct = async (id) => {
    try {
      await hideProduct(id);
      loadProducts();
    } catch {
      toast.error("Failed to update visibility");
    }
  };

  const moveProduct = async (index, direction) => {
    const updated = [...products];

    const target = direction === "up" ? index - 1 : index + 1;

    if (target < 0 || target >= updated.length) return;

    [updated[index], updated[target]] = [updated[target], updated[index]];

    const reordered = updated.map((p, i) => ({
      _id: p._id,
      order: i,
    }));

    setProducts(updated);

    try {
      await reorderProducts(reordered);
    } catch {
      toast.error("Reorder failed");
      loadProducts();
    }
  };

  return (
    <DashboardLayout>
      <Box p={3}>
        <Typography variant="h5" fontWeight="bold">
          Product Manager
        </Typography>

        <form onSubmit={handleSubmit(handleAddProduct)}>
          <Stack spacing={2} mt={2}>
            <TextField
              label="Product Title"
              {...register("title")}
              error={!!errors.title}
              helperText={errors.title?.message}
            />

            <TextField
              label="Description"
              multiline
              rows={3}
              {...register("description")}
              error={!!errors.description}
              helperText={errors.description?.message}
            />

            <Button type="submit" variant="contained">
              Add Product
            </Button>
          </Stack>
        </form>

        <Stack spacing={2} mt={5}>
          {products.map((product, index) => (
            <Card key={product._id} sx={{ opacity: product.hidden ? 0.4 : 1 }}>
              <CardContent>
                <Typography variant="h6">{product.title}</Typography>

                <Typography variant="body2">{product.description}</Typography>

                <Stack direction="row" spacing={1} mt={2}>
                  <IconButton onClick={() => moveProduct(index, "up")}>
                    <ArrowUpwardIcon />
                  </IconButton>

                  <IconButton onClick={() => moveProduct(index, "down")}>
                    <ArrowDownwardIcon />
                  </IconButton>

                  <IconButton onClick={() => handleHideProduct(product._id)}>
                    <VisibilityOffIcon />
                  </IconButton>

                  <IconButton onClick={() => handleDeleteProduct(product._id)}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>

        <ToastContainer />
      </Box>
    </DashboardLayout>
  );
}
